import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Volume2, Play, Pause, Check, Loader2, Mic, Gauge, Save } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const speedLabel = (s) => (s <= 0.8 ? "Très lente" : s <= 0.95 ? "Lente" : s <= 1.05 ? "Normale" : "Rapide");
const stabLabel = (s) => (s <= 0.35 ? "Expressive" : s <= 0.7 ? "Équilibrée" : "Très posée");
const pauseLabel = (p) => (p <= 0.05 ? "Aucune" : p <= 0.5 ? "Courtes" : p <= 1.0 ? "Moyennes" : "Longues");

export function VoiceSelector() {
  const [voices, setVoices] = useState([]);
  const [selected, setSelected] = useState("");
  const [speed, setSpeed] = useState(0.9);
  const [stability, setStability] = useState(0.6);
  const [pause, setPause] = useState(0.0);
  const [previewing, setPreviewing] = useState("");
  const [saving, setSaving] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [playing, setPlaying] = useState("");
  const cacheRef = useRef({});
  const audioRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/tts/voices")
      .then((r) => {
        setVoices(r.data.voices || []);
        setSelected(r.data.selected || "");
        if (typeof r.data.speed === "number") setSpeed(r.data.speed);
        if (typeof r.data.stability === "number") setStability(r.data.stability);
        if (typeof r.data.pause === "number") setPause(r.data.pause);
      })
      .catch(() => setVoices([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying("");
  };

  const playUrl = (id, url) => {
    stopAudio();
    const full = url.startsWith("http") ? url : `${BACKEND}${url}`;
    const audio = new Audio(full);
    audioRef.current = audio;
    audio.onended = () => setPlaying("");
    audio.play();
    setPlaying(id);
  };

  const listen = async (id) => {
    if (playing === id) { stopAudio(); return; }
    const key = `${id}_${speed}_${stability}_${pause}`;
    if (cacheRef.current[key]) { playUrl(id, cacheRef.current[key]); return; }
    setPreviewing(id);
    try {
      const r = await api.post("/admin/tts/preview", { voice_id: id, speed, stability, pause });
      cacheRef.current[key] = r.data.url;
      playUrl(id, r.data.url);
    } catch {
      toast.error("Impossible de générer l'aperçu de la voix.");
    } finally {
      setPreviewing("");
    }
  };

  const choose = async (id) => {
    setSaving(id);
    try {
      await api.post("/admin/tts/select", { voice_id: id, speed, stability, pause });
      setSelected(id);
      toast.success("Voix enregistrée pour toutes les lectures.");
    } catch {
      toast.error("Impossible d'enregistrer la voix.");
    } finally {
      setSaving("");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.post("/admin/tts/select", { speed, stability, pause });
      toast.success("Réglages de vitesse et de ton enregistrés.");
    } catch {
      toast.error("Impossible d'enregistrer les réglages.");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <Card className="border-gray-200 mb-8" data-testid="voice-selector">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Mic className="w-5 h-5 text-emerald-700" />
          <h2 className="font-serif-display text-xl font-normal text-gray-800">Voix de lecture</h2>
        </div>
        <p className="text-sm text-gray-500 mb-5">
          Choisissez la voix française et réglez la vitesse et le ton pour les contes, poésies et exercices de prononciation.
          Ralentir la lecture aide beaucoup les personnes âgées.
        </p>

        {/* Réglages vitesse & ton */}
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 mb-5" data-testid="voice-settings">
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="w-4 h-4 text-emerald-700" />
            <span className="font-semibold text-gray-800 text-sm">Réglages de lecture</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Vitesse</label>
                <span className="text-xs font-semibold text-emerald-700" data-testid="voice-speed-value">
                  {speedLabel(speed)} · {speed.toFixed(2)}×
                </span>
              </div>
              <Slider min={0.7} max={1.2} step={0.05} value={[speed]}
                onValueChange={(v) => setSpeed(v[0])} data-testid="voice-speed-slider" />
              <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                <span>Lent</span><span>Rapide</span>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Ton de la voix</label>
                <span className="text-xs font-semibold text-emerald-700" data-testid="voice-stability-value">
                  {stabLabel(stability)}
                </span>
              </div>
              <Slider min={0} max={1} step={0.05} value={[stability]}
                onValueChange={(v) => setStability(v[0])} data-testid="voice-stability-slider" />
              <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                <span>Expressive</span><span>Posée</span>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Pauses entre phrases</label>
                <span className="text-xs font-semibold text-emerald-700" data-testid="voice-pause-value">
                  {pauseLabel(pause)}
                </span>
              </div>
              <Slider min={0} max={1.5} step={0.1} value={[pause]}
                onValueChange={(v) => setPause(v[0])} data-testid="voice-pause-slider" />
              <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                <span>Aucune</span><span>Longues</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button size="sm" onClick={saveSettings} disabled={savingSettings}
              className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="voice-save-settings">
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Enregistrer les réglages</>}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {voices.map((v) => {
            const isSelected = selected === v.id;
            const isPlaying = playing === v.id;
            const isPreviewing = previewing === v.id;
            return (
              <div key={v.id} data-testid={`voice-row-${v.id}`}
                className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${isSelected ? "border-emerald-500 bg-emerald-50" : "border-gray-200 bg-white"}`}>
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                  <Volume2 className="w-5 h-5 text-emerald-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">{v.name}</span>
                    {isSelected && (
                      <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5" data-testid={`voice-active-${v.id}`}>
                        Voix active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{v.desc}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => listen(v.id)} disabled={isPreviewing}
                  className="rounded-full h-9 shrink-0" data-testid={`voice-listen-${v.id}`}>
                  {isPreviewing ? <Loader2 className="w-4 h-4 animate-spin" />
                    : isPlaying ? <><Pause className="w-4 h-4 mr-1" /> Stop</>
                    : <><Play className="w-4 h-4 mr-1" /> Écouter</>}
                </Button>
                <Button size="sm" onClick={() => choose(v.id)} disabled={isSelected || saving === v.id}
                  className={`rounded-full h-9 shrink-0 ${isSelected ? "bg-emerald-600" : "bg-emerald-700 hover:bg-emerald-800"}`}
                  data-testid={`voice-choose-${v.id}`}>
                  {saving === v.id ? <Loader2 className="w-4 h-4 animate-spin" />
                    : isSelected ? <><Check className="w-4 h-4 mr-1" /> Choisie</>
                    : "Choisir"}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
